import { Injectable } from '@angular/core'
import { forkJoin, Observable, of, switchMap } from 'rxjs'
import { catchError, shareReplay, take } from 'rxjs/operators'

import { AllocationResponse } from '../models/allocation-response.model'
import { CharacteristicResponse } from '../models/characteristic-response.model'
import { ClassificationResponse } from '../models/classification-response.model'
import { ContentResponse } from '../models/content-response.model'
import { Distribution } from '../models/distribution.model'
import { DiversificationResponse } from '../models/diversification-response.model'
import { ExpenseResponse } from '../models/expense-response.model'
import { FeeResponse } from '../models/fee-response.model'
import { MinimumResponse } from '../models/minimum-response.model'
import { PerformanceResponse } from '../models/performance-response.model'
import {
  DateRangeHoldingResponse,
  PortfolioHoldingResponse,
} from '../models/portfolio-models/portfolio-holding-response.model'
import { PriceHistoryResponse } from '../models/price-History-response.model'
import { PriceResponse } from '../models/price-response.model'
import { ProfileResponse } from '../models/profile-response.model'
import { RiskResponse } from '../models/risk-response.model'
import { AllocationService } from './allocation/allocation.service'
import { CharacteristicService } from './characteristic/characteristic.service'
import { ClassificationService } from './classification/classification.service'
import { FundContentService } from './content/fund-content.service'
import { DistributionService } from './distribution/distribution.service'
import { DiversificationService } from './diversification/diversification.service'
import { ExpenseService } from './expense/expense.service'
import { FeeService } from './fee/fee.service'
import { MinimumService } from './minimum/minimum.service'
import { PerformanceService } from './performance/performance.service'
import { PortfolioHoldingService } from './portfolio-holding/portfolio-holding.service'
import { PriceService } from './price/price.service'
import { PriceHistoryService } from './Price-History/price-history.service'
import { ProfileService } from './profile/profile.service'
import { RiskService } from './risk/risk.service'

@Injectable({
  providedIn: 'root',
})
export class MainService {
  tickerOrFundId: string
  dashboard$ = of([])
  portfolio$ = of([])
  perfObservable$ = of([])
  allPriceObservable$ = of([])
  historicalPriceObservable$ = of([])
  fundManagementObservable$ = of([])
  navTabGroupObservable$ = of([])
  fundLiteratureObservable$ = of([])
  profile$: Observable<ProfileResponse>
  minimum$: Observable<MinimumResponse>
  performance$: Observable<PerformanceResponse>
  price$: Observable<PriceResponse>
  risk$: Observable<RiskResponse>
  fee$: Observable<FeeResponse>
  fundContent$: Observable<ContentResponse>
  marketPriceContent$: Observable<any>
  portHolStock$: Observable<PortfolioHoldingResponse>
  portHolBond$: Observable<PortfolioHoldingResponse>
  portHolShortTerm$: Observable<PortfolioHoldingResponse>
  portHolCurrency$: Observable<PortfolioHoldingResponse>
  portHolDerivative$: Observable<PortfolioHoldingResponse>
  portHolCommodity$: Observable<PortfolioHoldingResponse>

  priceHistory1M$: Observable<PriceHistoryResponse>
  priceHistory3M$: Observable<PriceHistoryResponse>
  priceHistory6M$: Observable<PriceHistoryResponse>
  priceHistory1Y$: Observable<PriceHistoryResponse>
  priceHistory3Y$: Observable<PriceHistoryResponse>
  priceHistory5Y$: Observable<PriceHistoryResponse>
  priceHistory10Y$: Observable<PriceHistoryResponse>
  priceHistory$: Observable<PriceHistoryResponse>

  portHolStockDaily$: Observable<PortfolioHoldingResponse>
  portHolBondDaily$: Observable<PortfolioHoldingResponse>
  portHolShortTermDaily$: Observable<PortfolioHoldingResponse>
  portHolDerivativeDaily$: Observable<PortfolioHoldingResponse>

  portHolMoneyMarket$: Observable<PortfolioHoldingResponse>
  portHolDateRange$: Observable<DateRangeHoldingResponse>

  diversification$: Observable<DiversificationResponse>
  allocation$: Observable<AllocationResponse>
  classification$: Observable<ClassificationResponse>
  characteristic$: Observable<CharacteristicResponse>
  expense$: Observable<ExpenseResponse>
  distribution$: Observable<Distribution>
  distObservable$: Observable<[ProfileResponse, Distribution]>
  discObservable$: Observable<[ProfileResponse, RiskResponse, ContentResponse]>
  overviewObservable$ = of([])
  // eslint-disable-next-line max-params
  constructor(
    private readonly profileService: ProfileService,
    private readonly minimumService: MinimumService,
    private readonly performanceService: PerformanceService,
    private readonly priceService: PriceService,
    private readonly riskService: RiskService,
    private readonly portfolioHoldingService: PortfolioHoldingService,
    private readonly diversificationService: DiversificationService,
    private readonly characteristicService: CharacteristicService,
    private readonly allocationService: AllocationService,
    private readonly classificationService: ClassificationService,
    private readonly expenseService: ExpenseService,
    private readonly fundContentService: FundContentService,
    private readonly distributionService: DistributionService,
    private readonly priceHistoryService: PriceHistoryService,
    private readonly feeService: FeeService,
  ) {}

  getProfileData() {
    const profileDataElement = document.getElementById('fundProfileData')
    if (profileDataElement && profileDataElement.textContent) {
      const profileData = JSON.parse(profileDataElement.textContent)

      this.profile$ = of(profileData).pipe(
        switchMap((profile: ProfileResponse) => {
          this.profileService.setMeta(profile)
          return this.profileService.getAssociatedFundTickers(profile)
        }),
        shareReplay(),
      )
    }
  }
  // eslint-disable-next-line max-statements
  getFundData(tickerOrFundId: string, isInternal: boolean, isBfp: any) {
    this.tickerOrFundId = tickerOrFundId?.toUpperCase()
    if (this.tickerOrFundId) {
      this.risk$ = this.riskService
        .getRisk(this.tickerOrFundId)
        .pipe(shareReplay())
      this.minimum$ = this.minimumService
        .getMinimum(this.tickerOrFundId)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .pipe(
          shareReplay(),
          catchError((_e) => of(null)),
        )
      this.performance$ = this.performanceService
        .getPerformance(this.tickerOrFundId)
        .pipe(shareReplay())
      this.price$ = this.priceService
        .getDailyPrice(this.tickerOrFundId)
        .pipe(shareReplay())
      this.fundContent$ = this.fundContentService
        .getFundContent(this.tickerOrFundId)
        .pipe(shareReplay())
      this.marketPriceContent$ = this.fundContentService
        .getMarketPriceDisclosures()
        .pipe(shareReplay())
      this.getPortfolioHoldingData()
      this.getPriceHistoryData()
      this.diversification$ = this.diversificationService
        .getDiversification(
          this.tickerOrFundId,
          isInternal,
          isBfp?.diversification,
        )
        .pipe(shareReplay())
      this.characteristic$ = this.characteristicService
        .getCharacteristic(
          this.tickerOrFundId,
          isInternal,
          isBfp?.characteristics,
        )
        .pipe(shareReplay())
      this.allocation$ = this.allocationService
        .getAllocation(this.tickerOrFundId, isInternal, isBfp?.allocation)
        .pipe(shareReplay())
      this.expense$ = this.expenseService
        .getExpense(this.tickerOrFundId)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .pipe(
          shareReplay(),
          catchError((_e) => of(null)),
        )
      this.distribution$ = this.distributionService
        .getDistribution(this.tickerOrFundId)
        .pipe(shareReplay())
      this.fee$ = this.feeService
        .getFee(this.tickerOrFundId)
        .pipe(shareReplay())
      this.classification$ = this.classificationService
        .getClassification(
          this.tickerOrFundId,
          isInternal,
          isBfp?.classification,
        )
        .pipe(shareReplay())

      this.dashboard$ = forkJoin([
        this.risk$,
        this.minimum$,
        this.performance$,
        this.price$,
        this.expense$,
        this.fundContent$,
        this.marketPriceContent$,
      ]).pipe(take(1))

      this.overviewObservable$ = forkJoin([
        this.profile$,
        this.risk$,
        this.performance$,
        this.price$,
        this.fundContent$,
        this.minimum$,
        this.expense$,
        this.marketPriceContent$,
      ]).pipe(take(1))

      this.allPriceObservable$ = forkJoin([
        this.profile$,
        this.price$,
        this.fundContent$,
        this.marketPriceContent$,
      ]).pipe(take(1))

      this.historicalPriceObservable$ = forkJoin([
        this.priceHistory1M$,
        this.priceHistory3M$,
        this.priceHistory6M$,
        this.priceHistory1Y$,
        this.priceHistory3Y$,
        this.priceHistory5Y$,
        this.priceHistory10Y$,
      ]).pipe(take(1))

      this.perfObservable$ = forkJoin([
        this.profile$,
        this.performance$,
        this.expense$,
        this.fee$,
        this.minimum$,
        this.fundContent$,
        this.risk$,
        this.marketPriceContent$,
      ]).pipe(take(1))

      this.distObservable$ = forkJoin([this.profile$, this.distribution$]).pipe(
        take(1),
      )

      this.discObservable$ = forkJoin([
        this.profile$,
        this.risk$,
        this.fundContent$,
      ]).pipe(take(1))

      this.fundManagementObservable$ = forkJoin([
        this.fundContent$,
        this.profile$,
      ]).pipe(take(1))
      this.navTabGroupObservable$ = forkJoin([this.profile$]).pipe(take(1))

      this.fundLiteratureObservable$ = forkJoin([this.profile$]).pipe(take(1))

      this.portfolio$ = forkJoin([
        this.profile$,
        this.portHolStock$,
        this.portHolBond$,
        this.portHolShortTerm$,
        this.portHolCurrency$,
        this.portHolDerivative$,
        this.portHolCommodity$,
        this.diversification$,
        this.characteristic$,
        this.risk$,
        this.allocation$,
        this.portHolStockDaily$,
        this.portHolBondDaily$,
        this.portHolShortTermDaily$,
        this.portHolDerivativeDaily$,
        this.portHolMoneyMarket$,
        this.portHolDateRange$,
        this.classification$,
        this.fundContent$,
        this.marketPriceContent$,
      ])
    }
  }

  getDashboardObservable(): Observable<any[]> {
    return this.dashboard$
  }

  getProfileObservable(): Observable<ProfileResponse> {
    return this.profile$
  }
  getPortfolioObservable(): Observable<any[]> {
    return this.portfolio$
  }

  getPerformanceObservable(): Observable<any[]> {
    return this.perfObservable$
  }

  getDistributionObservable(): Observable<any> {
    return this.distObservable$
  }

  getDisclaimerObservable(): Observable<any> {
    return this.discObservable$
  }

  getPriceObservable(): Observable<any[]> {
    return this.allPriceObservable$
  }

  getHistoricalPriceObservable(): Observable<any[]> {
    return this.historicalPriceObservable$
  }

  getOverviewObservable(): Observable<any[]> {
    return this.overviewObservable$
  }

  getPriceHistoryData() {
    this.priceHistory1M$ = this.priceHistoryService
      .getPriceHistory(this.tickerOrFundId, '1M')
      .pipe(shareReplay())
    this.priceHistory3M$ = this.priceHistoryService
      .getPriceHistory(this.tickerOrFundId, '3M')
      .pipe(shareReplay())
    this.priceHistory6M$ = this.priceHistoryService
      .getPriceHistory(this.tickerOrFundId, '6M')
      .pipe(shareReplay())
    this.priceHistory1Y$ = this.priceHistoryService
      .getPriceHistory(this.tickerOrFundId, '1Y')
      .pipe(shareReplay())
    this.priceHistory3Y$ = this.priceHistoryService
      .getPriceHistory(this.tickerOrFundId, '3Y')
      .pipe(shareReplay())
    this.priceHistory5Y$ = this.priceHistoryService
      .getPriceHistory(this.tickerOrFundId, '5Y')
      .pipe(shareReplay())
    this.priceHistory10Y$ = this.priceHistoryService
      .getPriceHistory(this.tickerOrFundId, '10Y')
      .pipe(shareReplay())
  }

  getFundManagementObservable(): Observable<any[]> {
    return this.fundManagementObservable$
  }

  getNavTabGroupObservable(): Observable<any[]> {
    return this.navTabGroupObservable$
  }

  getFundLiteratureObservable(): Observable<any[]> {
    return this.fundLiteratureObservable$
  }

  getPortfolioHoldingData() {
    this.portHolStock$ = this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'stock', {})
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>
    this.portHolBond$ = this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'bond', {})
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>
    this.portHolShortTerm$ = this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'short-term-reserve', {})
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>
    this.portHolCurrency$ = this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'currency', {})
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>
    this.portHolDerivative$ = this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'derivative', {})
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>
    this.portHolCommodity$ = this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'commodity', {})
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>

    this.portHolStockDaily$ = this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'stock', { asOfType: 'daily' })
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>
    this.portHolBondDaily$ = this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'bond', { asOfType: 'daily' })
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>

    this.portHolShortTermDaily$ = this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'short-term-reserve', {
        asOfType: 'daily',
      })
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>

    this.portHolDerivativeDaily$ = this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'derivative', {
        asOfType: 'daily',
      })
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>

    this.portHolMoneyMarket$ = this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'money-market', {})
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>

    this.portHolDateRange$ = this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'date-range', {})
      .pipe(shareReplay()) as Observable<DateRangeHoldingResponse>
  }

  getMoneyMarketByDateRange(
    dateRange: string,
  ): Observable<PortfolioHoldingResponse> {
    return this.portfolioHoldingService
      .getPortfolioHoldings(this.tickerOrFundId, 'money-market', {
        date: dateRange,
      })
      .pipe(shareReplay()) as Observable<PortfolioHoldingResponse>
  }
}
